import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { RESOURCES_PATH } from 'shared/consts';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { useIsMapViewEnabled } from 'hooks/use-is-map-view-enabled.hook';

/**
 * Puts the body's scroll back the way the resources page needs it once a surface that borrowed it —
 * the location sheet, the date and time page — goes away.
 *
 * Cleanup only, and only on unmount: while the borrower is on screen it owns the body, so writing
 * anything before then would fight it.
 *
 * The state is read through a ref rather than the effect's closure. With it in the closure, a change
 * of the effective map view ran this cleanup with the value it had *before* the change and re-applied
 * a lock nobody wanted — and since `useIsMapViewEnabled` turns off once the floorplan redesign takes
 * over, that left the page unable to scroll while it was still mounted.
 */
export const useMapViewOverflow = (): void => {
  const { pathname } = useLocation();
  const isMapViewEnabled = useIsMapViewEnabled();
  const isMobileDevice = useIsSmallViewportWidth();
  const isResourcesPage = pathname.includes(RESOURCES_PATH);

  const shouldLockRef = useRef(false);

  shouldLockRef.current = isResourcesPage && isMapViewEnabled && isMobileDevice;

  useEffect(
    () => () => {
      document.body.style.overflow = shouldLockRef.current ? 'hidden' : 'initial';
    },
    [],
  );
};
